import { useMemo } from "react";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { engagementRoles, shippedProof, principles, socialLinks } from "@/data/team";
import { bookingHref, bookingTarget, handleBookingClick } from "@/lib/booking";
import HeroFields from "@/components/HeroFields";

const About = () => {
  const structuredData = useMemo(() => {
    const sameAs = Object.values(socialLinks).filter(Boolean);
    return {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About Skandor Technologies",
      url: "https://www.skandortech.ai/about",
      mainEntity: {
        "@type": "Organization",
        name: "Skandor Technologies",
        url: "https://www.skandortech.ai",
        logo: "https://www.skandortech.ai/favicon.ico",
        ...(sameAs.length ? { sameAs } : {}),
      },
    };
  }, []);

  return (
    <PageShell
      title="About Skandor | A senior operating partner"
      description="Skandor is a senior operating partner for growth-stage product companies. We diagnose the whole flow, build the constraint with one accountable team, and stay until your people run it."
      canonicalPath="/about"
      structuredData={structuredData}
    >
      <section className="relative overflow-hidden bg-background py-16 md:py-24">
        <HeroFields compact />
        <div className="container relative z-10 mx-auto">
          <div className="max-w-4xl">
            <p className="eyebrow">About Skandor</p>
            <h1 className="mt-4">A senior operating partner for growth-stage product companies.</h1>
            <p className="lede mt-5">Skandor exists for the moment a product outgrows the systems underneath it. We diagnose the whole flow, build the constraint with one accountable team, and stay until your people run it.</p>
          </div>
        </div>
      </section>

      <section className="section-tint py-16 md:py-24">
        <div className="container mx-auto">
          <h2>What we believe</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {principles.map(([lead, body]) => (
              <article key={lead} className="surface-card p-6">
                <h3>{lead}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-2">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-16 md:py-24">
        <div className="container mx-auto">
          <h2>How we staff an engagement</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 min-[1000px]:grid-cols-5">
            {engagementRoles.map(([key, role, body]) => (
              <article key={role} className="surface-card p-5">
                <p className="font-mono text-[.66rem] text-steel-2">{key}</p>
                <h3 className="mt-3">{role}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-2">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-tint py-16 md:py-24">
        <div className="container mx-auto">
          <h2>Where our people have shipped</h2>
          <ul className="mt-8 grid max-w-4xl gap-3">
            {shippedProof.map((item) => (
              <li key={item} className="surface-card flex gap-3 p-5 text-sm leading-relaxed text-ink-2">
                <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-teal" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-background py-16 md:py-24">
        <div className="container mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
          <div>
            <h2>How we measure</h2>
            <p className="mt-4 text-ink-2">Every engagement starts with a two-week diagnostic and a 12-week pre-engagement baseline. Outcomes are measured weekly against it, and fees are tied to that measurement.</p>
          </div>
          <div>
            <h2>Where we work</h2>
            <p className="mt-4 text-ink-2">Headquartered in New York, working with clients worldwide. Engagements run remotely across time zones, with on-site diagnostics where the operation needs it.</p>
          </div>
        </div>
      </section>

      <section className="section-tint pb-16 pt-4 md:pb-24">
        <div className="container mx-auto">
          <div className="gradient-frame mx-auto max-w-3xl"><div className="surface-card p-8 text-center md:p-10">
            <h2>Meet the team on the diagnostic call.</h2>
            <div className="mt-6 flex justify-center">
              <Button asChild size="lg">
                <a href={bookingHref} target={bookingTarget} rel={bookingTarget ? "noopener noreferrer" : undefined} onClick={(e) => handleBookingClick(e, "about_page")}>
                  Book a 30-min diagnostic call
                </a>
              </Button>
            </div>
           <p className="mt-4 text-sm text-ink-3">References available under NDA.</p>
          </div></div>
        </div>
      </section>
    </PageShell>
  );
};
export default About;
